import React, { useState, useEffect } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  addToast
} from "@heroui/react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { editTranscriptionAsync } from "../../redux/transcription";
import AppButton from "../common/AppButton";
import { uploadInBlocks } from "../../utils/blockUpload";
import { forgetUpload, pendingUploadFor, rememberUpload } from "../../utils/pendingUploads";

const UploadDrawer = ({ isOpen, onClose, session }) => {
  const [fileMp3, setFileMp3] = useState(null);
  const [fileRtf, setFileRtf] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgressMp3, setUploadProgressMp3] = useState(0);
  const [uploadProgressRtf, setUploadProgressRtf] = useState(0);
  const [isDraggingMp3, setIsDraggingMp3] = useState(false);
  const [isDraggingRtf, setIsDraggingRtf] = useState(false);
  const dispatch = useDispatch();

  const handleFileChangeMp3 = (e) => {
    setFileMp3(e.target.files[0]);
  };

  const handleFileChangeRtf = (e) => {
    setFileRtf(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!session) return;
    // Pressing Upload without a file used to do nothing at all.
    if (!fileMp3) {
      addToast({ title: "Bitte zuerst eine Audiodatei auswählen", color: "warning" });
      return;
    }
    setUploading(true);

    try {
      // Upload MP3
      const resMp3 = await axios.get(`transcriptions_poc_000113_1_0_0/transcription/get-url-mp3/${session.id}.mp3`);
      const { url: urlMp3 } = resMp3.data;
      if (!urlMp3) throw new Error("SAS URL for MP3 not received");

      // Continue an interrupted upload of this same file to this session.
      const resume = pendingUploadFor(fileMp3) === session.id;
      rememberUpload(fileMp3, session.id);
      await uploadInBlocks(urlMp3, fileMp3, {
        resume,
        onProgress: (sent, total) =>
          setUploadProgressMp3(Math.round((sent * 100) / total)),
      });

      // The RTF is optional, as in the new-session form: the transcription
      // then runs without the speaker names it carries. This drawer is how
      // a session whose upload broke off ("Waiting upload") gets its audio.
      if (fileRtf) {
        const resRtf = await axios.get(`transcriptions_poc_000113_1_0_0/transcription/get-url-rtf/${session.id}.rtf`);
        const { url: urlRtf } = resRtf.data;
        if (!urlRtf) throw new Error("SAS URL for RTF not received");

        await axios.put(urlRtf, fileRtf, {
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": fileRtf.type,
          },
          onUploadProgress: (e) => {
            const percent = Math.round((e.loaded * 100) / e.total);
            setUploadProgressRtf(percent);
          },
        });
      }

      await dispatch(
        editTranscriptionAsync({
          id: session.id,
          payload: {
            stage: "Transcription pending",
            original_file_name: fileMp3.name,
          },
        })
      );

      forgetUpload(fileMp3);
      console.log("✅ Upload complete for MP3 and RTF, session updated.");
      addToast({
        title: "Dateien erfolgreich hochgeladen",
        color: "success"
      });
      onClose();
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Something went wrong during upload.");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setFileMp3(null);
      setFileRtf(null);
      setUploadProgressMp3(0);
      setUploadProgressRtf(0);
      setIsDraggingMp3(false);
      setIsDraggingRtf(false);
    }
  }, [isOpen]);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right">
      <DrawerContent className="p-4 space-y-4">
        <DrawerHeader>Upload Files for Session {session?.name}</DrawerHeader>
        <DrawerBody className="space-y-6">
          {/* MP3 Upload */}
          <div
            className="relative w-full"
            onDragOver={(e) => {
              e.preventDefault();
              setIsDraggingMp3(true);
            }}
            onDragLeave={() => setIsDraggingMp3(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDraggingMp3(false);
              const droppedFile = e.dataTransfer.files[0];
              if (droppedFile) {
                setFileMp3(droppedFile);
              }
            }}
          >
            <input
              id="file-upload-mp3"
              type="file"
              accept=".mp3"
              onChange={handleFileChangeMp3}
              className="hidden"
            />
            <label
              htmlFor="file-upload-mp3"
              className={`block w-full h-32 px-6 py-6 border-2 rounded-md transition text-gray-700 text-lg font-semibold text-center flex items-center justify-center cursor-pointer ${
                isDraggingMp3
                  ? "bg-secondary"
                  : "hover:bg-secondary bg-secondaryContainer text:onSecondaryContainer hover:text-onSecondary"
              }`}
            >
              {isDraggingMp3
                ? "Drop the MP3 file here 🎵"
                : fileMp3
                ? fileMp3.name
                : "Click to browse or drop MP3 file here"}
            </label>

            {/* MP3 Progress */}
            {uploadProgressMp3 > 0 && (
              <>
                <div className="text-sm text-gray-600 mt-2">
                  MP3 Uploading: {uploadProgressMp3}%
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                  <div
                    className="bg-secondaryContainer h-2.5 rounded-full"
                    style={{ width: `${uploadProgressMp3}%` }}
                  ></div>
                </div>
              </>
            )}
          </div>

          {/* RTF Upload */}
          <div
            className="relative w-full"
            onDragOver={(e) => {
              e.preventDefault();
              setIsDraggingRtf(true);
            }}
            onDragLeave={() => setIsDraggingRtf(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDraggingRtf(false);
              const droppedFile = e.dataTransfer.files[0];
              if (droppedFile) {
                setFileRtf(droppedFile);
              }
            }}
          >
            <input
              id="file-upload-rtf"
              type="file"
              accept=".rtf"
              onChange={handleFileChangeRtf}
              className="hidden"
            />
            <label
              htmlFor="file-upload-rtf"
              className={`block w-full h-32 px-6 py-6 border-2 rounded-md transition text-gray-700 text-lg font-semibold text-center flex items-center justify-center cursor-pointer ${
                isDraggingRtf
                  ? "bg-secondary"
                  : "hover:bg-secondary bg-secondaryContainer text:onSecondaryContainer hover:text-onSecondary"
                  
              }`}
            >
              {isDraggingRtf
                ? "Drop the RTF file here 📄"
                : fileRtf
                ? fileRtf.name
                : "Click to browse or drop RTF file here"}
            </label>

            {/* RTF Progress */}
            {uploadProgressRtf > 0 && (
              <>
                <div className="text-sm text-gray-600 mt-2">
                  RTF Uploading: {uploadProgressRtf}%
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                  <div
                    className="bg-secondaryContainer h-2.5 rounded-full"
                    style={{ width: `${uploadProgressRtf}%` }}
                  ></div>
                </div>
              </>
            )}
          </div>

        </DrawerBody>
        <DrawerFooter className="flex justify-end gap-2">
          <Button color="danger" variant="bordered" radius="full" onPress={onClose}>
            Cancel
          </Button>
          <AppButton
            onClick={handleUpload}
            width="w-[120px]"
            rounded="rounded-[16px]"
            disabled={uploading}
          >
            Upload
          </AppButton>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default UploadDrawer;
